<template>
  <div class="clearfix">
    <div
      id="engineerSubmissionModal"
      ref="engineerSubmissionModal"
      class="reveal preview-submission-modal"
      data-reveal
    >
      <button
        class="close-button"
        data-close
        aria-label="Close modal"
        type="button"
      >
        <span aria-hidden="true">&times;</span>
      </button>
      <div class="tender-confirm clearfix">
        <div class="submission-email-subject">
          <label>{{ $t('eCircular.subject') }}</label>
          <div>{{ $t('tenantProjects.submission.submissionTitle', [Number(displayBatchIndex) + 1, currentTenantProject.projectName]) }}</div>
        </div>
        <div
          v-if="canSend"
          class="receivers-view-list"
        >
          <div class="text-input">
            <label class="required-flag">{{ $t('to') }}</label>
            <textarea
              v-hkldinput
              v-autosize
              v-fusioninputcheck="{
                type: 'string',
                tip: invalidEmailTip,
                pattern: validateEmails,
                classes: 'tooltip-class',
                required: true
              }"
              class="autosize"
              :value="displayTo"
              :required="true"
              @input="event => updateEmails(event, 'to')"
            />
          </div>

          <div class="text-input">
            <label>{{ $t('cc') }}</label>
            <textarea
              v-hkldinput
              v-autosize
              v-fusioninputcheck="{
                type: 'string',
                tip: invalidEmailTip,
                pattern: validateEmails,
                classes: 'tooltip-class',
                required: cc.trim()
              }"
              class="autosize"
              :value="displayCC"
              :required="cc.trim()"
              @input="event => updateEmails(event, 'cc')"
            />
          </div>

          <div class="text-input">
            <label>{{ $t('bcc') }}</label>
            <textarea
              v-hkldinput
              v-autosize
              v-fusioninputcheck="{
                type: 'string',
                tip: invalidEmailTip,
                pattern: validateEmails,
                classes: 'tooltip-class',
                required: bcc.trim()
              }"
              class="autosize"
              :value="displayBCC"
              :required="bcc.trim()"
              @input="event => updateEmails(event, 'bcc')"
            />
          </div>
        </div>

        <div class="comments-preview-body-warpper">
          <CommentsPreviewBody
            :isBatchesView="false"
            :activeBatch="submissions[displayBatchIndex]"
          />
        </div>

        <TenantSubmissionDocuments
          :files.sync="files"
          :sourceId="(submissions[displayBatchIndex] || {}).id || ''"
          :canEdit="false"
        />
        <div
          v-if="canSend"
          class="row align-right nav_btn"
        >
          <button
            id="confirm_no"
            v-hkldreset
            type="button"
            class="btn btn--darkblue"
            @click="saveReceivers()"
          >{{ $t('action.save') }}</button>
          <button
            id="confirm_ok"
            v-hkldreset
            type="button"
            class="btn btn--orange"
            @click="resend()"
          >{{ $t('tenantProjects.resendToTenant') }}</button>
        </div>
        <div
          v-if="currentTenantProject.type === tenantProjectTypes.Fitout && !isDesignCommentFulfilled"
          class="error-msg"
        >
          {{ designCommentsErrMsg }}
        </div>
        <div class="submission-send-history">
          <div class="table-label">
            {{ $t('tenantProjects.sendHistory') }}
          </div>
          <table class="history-table">
            <thead>
              <tr>
                <th
                  v-for="(header, index) in sendHisotryTableHeaders"
                  :key="index"
                  :class="header.class"
                >
                  {{ header.title }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(history, index) in sendActionHistories"
                :key="index"
              >
                <td>{{ deep(history, 'user.displayName') }}</td>
                <td>{{ deep(history, 'emailReceivers.to').replace(/;/g, '; ') }}</td>
                <td>{{ deep(history, 'emailReceivers.cc').replace(/;/g, '; ') }}</td>
                <td>{{ deep(history, 'emailReceivers.bcc').replace(/;/g, '; ') }}</td>
                <td style="white-space: nowrap;">
                  {{ deep(history, 'actionType') | customStatusLabel | capitalize }}
                </td>
                <td>{{ dateUtil.formatDate(deep(history, 'updatedAt'), true) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <CommentHistory :submission="submissions[displayBatchIndex]" />
      </div>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'

import CommentHistory from './CommentHistory'
import CommentsPreviewBody from './CommentsPreviewBody'
import TenantSubmissionDocuments from './TenantSubmissionDocuments'
import { CommentActionTypes } from './consts'

import * as regex from '@/utils/regex'
import dateUtil from '@/utils/dateUtil'
import numberUtils from '@/utils/numberUtils'
import mutationTypes from '@/store/mutation-types'
import previewSubmissionModalMixin from './previewSubmissionModalMixin'
import { aclHelper, tmsPermsTypes } from '@/utils/aclHelper'
import { EmailTypes } from '@/utils/consts'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { getSameBuildingUsers } from '@/api/users/request'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { processDuplicateEmails } from '@/api/postman/util'
import { sendEmail } from '@/api/postman/request'
import { tenantProjectStatus, tenantProjectTypes } from '@/utils/locale'
import { utils as formUtils } from '@/plugin/InputChange'

// For historical fit out. It means that the pop-up only for resend function
export default {
  name: 'EngineerSubmissionModal',
  components: {
    CommentHistory,
    CommentsPreviewBody,
    TenantSubmissionDocuments,
  },
  mixins: [previewSubmissionModalMixin],
  data () {
    return {
      lodash,
      deep,
      dateUtil,
      to: '',
      cc: '',
      bcc: '',
      regex,
      files: [],
      displayBatchIndex: -1,
      isDesignCommentFulfilled: true,
      designCommentsErrMsg: '',
      tenantProjectTypes,
    }
  },
  methods: {
    ...mapActions('tenantProject', [
      'loadTenantProject',
      'updateTenantSubmission',
      'logResentAction',
    ]),
    ...mapMutations('tenantProject', {
      updateFieldByPath: mutationTypes.TENANT_PROJECT_UPDATE_FIELD_PATH,
    }),
    async init () {
      if (!this.engineerSubmissionModal) {
        this.engineerSubmissionModal = null
        this.engineerSubmissionModal = await new Foundation.Reveal($('#engineerSubmissionModal'), {
          closeOnEsc: false,
          closeOnClick: false,
        })
      }
    },
    async openModal (displayBatchIndex) {
      this.displayBatchIndex = displayBatchIndex
      await this.init()
      await this.setupReceivers()
      this.engineerSubmissionModal.open()
      setTimeout(formUtils.resizeElements, 0)
      setTimeout(() => { fusionInputCheckUtil.validateAllFields(undefined, '#engineerSubmissionModal') }, 0)
    },
    closeModal () {
      if (this.engineerSubmissionModal) {
        this.engineerSubmissionModal.close()
      }
    },
    getAlphabet (val) {
      return `${numberUtils.convertToAlphabet(parseInt(val + 1))}.`
    },
    getPrefix (index) {
      return `${index + 1}.`
    },
    getPath () {
      return ['submissions', this.displayBatchIndex, 'techComment', 'emailReceivers']
    },
    concatUserEmails (users) {
      const uniqUsers = lodash.uniqBy(users, 'id')
      return uniqUsers.reduce((result, user) => {
        const prefix = result === '' ? '' : this.emailSeparator
        return result.concat(prefix).concat(user.username)
      }, '')
    },
    async getEmailsForSameBuilding (permTypes) {
      if (this.tenancy && this.tenancy.type) {
        const users = await getSameBuildingUsers(this.tenancy.building.id, this.tenancy.type, false, permTypes)
        return users
      }
      return []
    },
    setupTos () {
      if (this.emailReceivers) {
        this.to = this.emailReceivers.to
      } else {
        this.to = this.concatUserEmails(this.accounts)
      }
    },
    setupReceivers () {
      this.setupTos()
      if (this.emailReceivers) {
        this.cc = this.emailReceivers.cc
        if (!this.cc.includes(this.currentUserEmail)) {
          this.cc += `;${this.currentUserEmail}`
        }
        const ccArray = this.cc.split(this.emailSeparator)
        const bccArray = this.emailReceivers.bcc.split(this.emailSeparator)
        this.bcc = bccArray.filter(email => !ccArray.includes(email)).join(this.emailSeparator)
      } else {
        this.cc = this.currentUserEmail
      }
      this.removeDuplicateEmailsInFields()
    },
    setReceiversToStore () {
      this.updateFieldByPath({
        path: this.getPath(),
        newValue: this.getUniqueEmails(),
      })
    },
    async saveReceivers () {
      if (!this.validate()) {
        return
      }
      this.setReceiversToStore()
      await this.updateTenantSubmission(this.displayBatchIndex)
      this.loadTenantProject(this.$router.currentRoute.params.id)
    },
    validate () {
      fusionInputCheckUtil.validateAllFields(undefined, '#engineerSubmissionModal')
      return fusionInputCheckUtil.isAllFieldsValid('.receivers-view-list')
    },
    async resend () {
      if (!this.validate()) {
        return
      }
      this.setReceiversToStore()
      this.logResentAction({
        content: {
          actionType: CommentActionTypes.RESEND,
          emailReceivers: this.emailReceivers,
        },
        submissionIndex: this.displayBatchIndex,
      })
      await this.updateTenantSubmission(this.displayBatchIndex)
      this.loadTenantProject(this.$router.currentRoute.params.id)
      const extMetaData = {
        to: this.splitEmails(this.to),
        cc: this.splitEmails(this.cc),
        bcc: this.splitEmails(this.bcc),
        docIds: this.files.map(file => file.id),
        action: CommentActionTypes.RESEND,
        submissionIndex: this.displayBatchIndex,
      }
      processDuplicateEmails(extMetaData.to, extMetaData.cc, extMetaData.bcc)
      await sendEmail(EmailTypes.tenantProjects.completeSubmission, extMetaData, false)
      this.closeModal()
    },
  },
  destroyed () {
    if (this.engineerSubmissionModal) {
      this.engineerSubmissionModal.destroy()
      delete this.engineerSubmissionModal
    }
    // @TODO Replace this modal with <HKLModal />
    $('.reveal-overlay > #engineerSubmissionModal').parent().remove()
  },
  computed: {
    ...mapGetters('tenantProject', ['currentTenantProject', 'accounts']),
    ...mapGetters('user', ['currentUserEmail']),
    sendHisotryTableHeaders () {
      return [
        {
          title: this.$t('sender'),
          class: 'header-sender',
        },
        {
          title: this.$t('to'),
          class: 'header-to',
        },
        {
          title: this.$t('cc'),
          class: 'header-to',
        },
        {
          title: this.$t('bcc'),
          class: 'header-to',
        }, {
          title: this.$t('tenancyProject.status'),
          class: 'header-status',
        }, {
          title: this.$t('time'),
          class: 'header-date',
        },
      ]
    },
    submissions () {
      const submissions = lodash.filter(deep(this.currentTenantProject, 'submissions'), (item) => this.isTenant ? true : item.completedDate) || []
      return lodash.orderBy(submissions, ['order'])
    },
    displayTenantSubmission () {
      return this.submissions[this.displayBatchIndex] || {}
    },
    techComment () {
      return lodash.get(this.submissions, [this.displayBatchIndex, 'techComment'], {})
    },
    designComment () {
      return (this.submissions && this.submissions[this.displayBatchIndex].designComment) || {}
    },
    comments () {
      return lodash.get(this.submissions, [this.displayBatchIndex, 'techComment', 'comments'], [])
    },
    emailReceivers () {
      return this.techComment && this.techComment.emailReceivers
    },
    tenancy () {
      return this.currentTenantProject && this.currentTenantProject.tenancy
    },
    historyCommenters () {
      return (this.submissions[this.displayBatchIndex].actionHistory || []).map(history => history.user) || []
    },
    sendActionHistories () {
      const sendHistories = lodash.get(this.displayTenantSubmission, 'actionHistory', []).filter(history => { if ([CommentActionTypes.SEND, CommentActionTypes.RESEND].includes(history.actionType)) return history })
      return lodash.orderBy(sendHistories, {'updatedAt': 'desc'})
    },
    status () {
      return lodash.get(this.currentTenantProject, 'status', '').toLocaleUpperCase()
    },
    canSend () {
      return this.status !== tenantProjectStatus.CANCELLED &&
        this.status !== tenantProjectStatus.WITHDRAWN &&
        aclHelper.getAccess(tmsPermsTypes.project.send)
    },
  },
}
</script>

<style scoped lang="scss">
@import '~styles/settings';
@import '~styles/popup';
</style>

<style lang="scss">
.tooltip {
  z-index: 1200;
}
#engineerSubmissionModal {
  $tender-font-color: #0C3A58;
  overflow: auto;
  width:90%;
  .submission-email-subject {
    & > label {
      color: $tender-font-color;
      font-size: 14px;
      font-weight: bold;
    }
  }
  .text-input {
    margin: 0 1px;
    textarea {
      border: solid 1px #B5B5B5;
      min-height: 20px;
      width: 95%;
      margin-bottom: 5px;
      height: 40px;
      color: $tender-font-color;
      overflow: hidden;
      padding-left: 10px;
    }

    &>label {
      text-transform: capitalize;
      margin-top: 15px;
      margin-bottom: 5px;
      font-size: 14px;
      line-height: 1.43;
      font-weight: bold;
      text-align: left;
      color: $tender-font-color;
    }
  }
  .consolidated-comment {
    .comment-content {
      padding: 10px 0;
      font-weight: normal;
    }
    .comment-content.description {
      padding: 0 0 10px 0;
    }
    .section-header {
      font-weight: bold;
      padding: 12px 0;
    }
  }
  .comments-preview-body-warpper {
    margin-top: 24px;
  }

  .error-msg {
    color: red;
    text-align: right;
    margin-top: 10px;
  }
  .submission-send-history {
    .table-label {
      font-weight: bold;
      margin-bottom: 5px;
    }
    .history-table {
      border: solid 1px #e0e0e0;
      border-spacing: 0px;
      border-collapse: collapse;
      table-layout: fixed;
      word-wrap: break-word;
      th {
        color: #0C3A58;
      }
      .header-date {
        width: 15%;
      }
      .header-to {
        width: 20%
      }
      .header-sender {
        width: 15%;
      }
      .heander-status {
        width: 10%;
      }
    }
  }
}
</style>
